import { db } from './firebase';
import {
  collection, doc, setDoc, getDoc, getDocs, updateDoc, deleteDoc, onSnapshot, serverTimestamp, query, where, arrayUnion, arrayRemove
} from 'firebase/firestore';
import debounce from 'lodash.debounce';

// Types
export interface Column {
  id: string;
  title: string;
  cardIds: string[];
}
export interface CardType {
  id: string;
  type: 'note' | 'resource' | 'vuln' | 'tool' | 'payload';
  title: string;
  content: any;
  tags?: string[];
  createdAt: number;
  updatedAt: number;
}
export interface Board {
  id: string;
  name: string;
  columns: Column[];
  cards: { [cardId: string]: CardType };
  createdAt: number;
  updatedAt: number;
  members?: string[]; // userIds
  pendingInvites?: string[]; // emails
  ownerId?: string; // userId of the board owner
}

const getBoardsCollection = (userId: string) => collection(db, 'users', userId, 'huntersBoards');
const getBoardDoc = (userId: string, boardId: string) => doc(db, 'users', userId, 'huntersBoards', boardId);

// Helper to call the new API, fallback to Firestore if fails
async function apiFetch(path: string, options?: RequestInit) {
  try {
    const res = await fetch(path, options);
    if (!res.ok) throw new Error('API error');
    return await res.json();
  } catch (e) {
    return null;
  }
}

// One-time fetch for boards (no real-time listener)
export async function fetchBoardsOnce(userId: string): Promise<Board[]> {
  try {
    const apiRes = await apiFetch(`/api/hunters-board?userId=${encodeURIComponent(userId)}`);
    if (apiRes && Array.isArray(apiRes)) return apiRes;
  } catch (e) {
    console.warn('API fetch failed, falling back to Firestore:', e);
  }
  
  // Fallback to Firestore
  try {
    const snap = await getDocs(getBoardsCollection(userId));
    return snap.docs.map(doc => doc.data() as Board);
  } catch (error) {
    console.error('Failed to fetch boards from Firestore:', error);
    return [];
  }
}

// Helper to get the memberships collection
const getMembershipsCollection = () => collection(db, 'memberships');

// Fetch all boards where user is a member (collaborator) using the memberships collection
export async function fetchBoardsForMember(userId: string): Promise<Board[]> {
  const boards: Board[] = [];
  try {
    // Query the memberships collection for all boardIds where userId == currentUserId
    const membershipsQ = query(getMembershipsCollection(), where('userId', '==', userId));
    const membershipsSnap = await getDocs(membershipsQ);
    const boardIds = membershipsSnap.docs.map(doc => doc.data().boardId);
    // Fetch each board directly
    for (const { boardId, ownerId } of membershipsSnap.docs.map(doc => doc.data())) {
      if (ownerId && boardId) {
        const boardSnap = await getDoc(getBoardDoc(ownerId, boardId));
        if (boardSnap.exists()) {
          boards.push(boardSnap.data() as Board);
        }
      }
    }
  } catch (error) {
    console.error('Failed to fetch shared boards for member:', error);
  }
  return boards;
}

// Update getAllBoards to use the new fetchBoardsForMember
export async function getAllBoards(userId: string): Promise<Board[]> {
  // Fetch owned boards
  const owned = await fetchBoardsOnce(userId);
  // Fetch boards where user is a member (collaborator)
  const shared = await fetchBoardsForMember(userId);
  // Merge and deduplicate by board id
  const all = [...owned, ...shared.filter(b => !owned.some(o => o.id === b.id))];
  return all;
}

export async function getBoard(userId: string, boardId: string): Promise<Board | null> {
  try {
    const apiRes = await apiFetch(`/api/hunters-board?userId=${encodeURIComponent(userId)}&boardId=${encodeURIComponent(boardId)}`);
    if (apiRes && apiRes.id) return apiRes as Board;
  } catch (e) {
    console.warn('API fetch failed, falling back to Firestore:', e);
  }
  
  // Fallback to Firestore
  try {
    const snap = await getDoc(getBoardDoc(userId, boardId));
    return snap.exists() ? (snap.data() as Board) : null;
  } catch (error) {
    console.error('Failed to fetch board from Firestore:', error);
    return null;
  }
}

export async function saveBoard(userId: string, board: Board): Promise<void> {
  try {
    const apiRes = await apiFetch(`/api/hunters-board?userId=${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...board, ownerId: board.ownerId || userId })
    });
    if (apiRes && apiRes.success) return;
  } catch (e) {
    console.warn('API save failed, falling back to Firestore:', e);
  }
  
  // Fallback to Firestore
  try {
    await setDoc(getBoardDoc(userId, board.id), {
      ...board,
      ownerId: board.ownerId || userId,
      updatedAt: Date.now(),
      createdAt: board.createdAt || Date.now(),
    });
  } catch (error) {
    console.error('Failed to save board to Firestore:', error);
  }
}

export async function deleteBoard(userId: string, boardId: string): Promise<void> {
  try {
    const apiRes = await apiFetch(`/api/hunters-board?userId=${encodeURIComponent(userId)}&boardId=${encodeURIComponent(boardId)}`, {
      method: 'DELETE'
    });
    if (apiRes && apiRes.success) return;
  } catch (e) {
    console.warn('API delete failed, falling back to Firestore:', e);
  }
  
  // Fallback to Firestore
  try {
    await deleteDoc(getBoardDoc(userId, boardId));
  } catch (error) {
    console.error('Failed to delete board from Firestore:', error);
  }
}

export function subscribeToBoards(userId: string, callback: (boards: Board[]) => void) {
  // Real-time updates still use Firestore
  try {
    return onSnapshot(getBoardsCollection(userId), snap => {
      callback(snap.docs.map(doc => doc.data() as Board));
    }, (error) => {
      console.error('Real-time subscription error:', error);
      // Fallback to one-time fetch on error
      fetchBoardsOnce(userId).then(callback);
    });
  } catch (error) {
    console.error('Failed to subscribe to boards:', error);
    // Fallback to one-time fetch
    fetchBoardsOnce(userId).then(callback);
    return () => {}; // Return empty unsubscribe function
  }
}

// Debounced saveBoard to reduce Firestore writes
export const debouncedSaveBoard = debounce(async (userId: string, board: Board) => {
  await saveBoard(userId, board);
}, 1500); 

// Utility: Check if user exists by email
export async function getUserByEmail(email: string): Promise<{uid: string, name: string, email: string} | null> {
  const normalizedEmail = email.trim().toLowerCase();
  try {
    const q = query(collection(db, 'users'), where('email', '==', normalizedEmail));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const docSnap = snap.docs[0];
      return { uid: docSnap.id, ...docSnap.data() } as any;
    }
    return null;
  } catch (e) {
    console.error('Error fetching user by email:', e);
    return null;
  }
}

// Firestore: Invitations collection
export interface BoardInvitation {
  id: string;
  fromUserId: string;
  toUserId: string;
  toEmail: string;
  boardId: string;
  status: 'pending' | 'accepted' | 'declined';
  createdAt: number;
}

// Send invite
export async function sendBoardInvite(fromUserId: string, toEmail: string, boardId: string): Promise<{success: boolean, error?: string}> {
  try {
    const email = toEmail.trim().toLowerCase();
    if (!email) {
      return { success: false, error: 'Email address is required' };
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      return { success: false, error: 'Enter a valid email address' };
    }

    if (!fromUserId) {
      return { success: false, error: 'You must be signed in to send invites' };
    }

    const boardRef = doc(db, 'users', fromUserId, 'huntersBoards', boardId);
    const boardSnap = await getDoc(boardRef);
    if (!boardSnap.exists()) {
      return { success: false, error: 'Board not found' };
    }

    const board = boardSnap.data() as Board;

    if (board.ownerId && board.ownerId !== fromUserId) {
      return { success: false, error: 'Only the board owner can send invitations' };
    }

    const user = await getUserByEmail(email);
    if (!user) {
      return { success: false, error: 'User not found' };
    }

    const toUserId = user.uid;

    if (toUserId === fromUserId) {
      return { success: false, error: 'You cannot invite yourself' };
    }

    const pendingInvites = (board.pendingInvites || []).map(inv => inv.toLowerCase());
    if (pendingInvites.includes(email)) {
      return { success: false, error: 'This user has already been invited' };
    }

    const members = (board.members || []);
    if (members.includes(toUserId)) {
      return { success: false, error: 'This user is already a board member' };
    }

    const inviteRef = doc(collection(db, 'invitations'));
    const invite: BoardInvitation = {
      id: inviteRef.id,
      fromUserId,
      toUserId,
      toEmail: email,
      boardId,
      status: 'pending',
      createdAt: Date.now(),
    };

    await setDoc(inviteRef, invite);

    await updateDoc(boardRef, { pendingInvites: arrayUnion(email) });

    return { success: true };
  } catch (error: any) {
    console.error('Error sending board invite:', error);
    return { success: false, error: error?.message || 'Failed to send invite' };
  }
}

// Accept invite
export async function acceptBoardInvite(invitationId: string): Promise<{success: boolean, error?: string}> {
  const inviteRef = doc(db, 'invitations', invitationId);
  const inviteSnap = await getDoc(inviteRef);
  if (!inviteSnap.exists()) return { success: false, error: 'Invite not found' };
  const invite = inviteSnap.data() as BoardInvitation;
  // Add user to board members
  const boardOwnerRef = doc(db, 'users', invite.fromUserId, 'huntersBoards', invite.boardId);
  await updateDoc(boardOwnerRef, {
    members: arrayUnion(invite.toUserId),
    pendingInvites: arrayRemove(invite.toEmail)
  });
  // Mark invite as accepted
  await updateDoc(inviteRef, { status: 'accepted' });
  // Add to memberships collection for fast lookup
  const membershipRef = doc(collection(db, 'memberships'));
  await setDoc(membershipRef, {
    userId: invite.toUserId,
    boardId: invite.boardId,
    ownerId: invite.fromUserId,
    role: 'member',
    createdAt: Date.now(),
  });
  return { success: true };
}

// Fetch invitations for a user
export async function getInvitationsForUser(userId: string): Promise<BoardInvitation[]> {
  const q = query(collection(db, 'invitations'), where('toUserId', '==', userId), where('status', '==', 'pending'));
  const snap = await getDocs(q);
  return snap.docs.map(doc => doc.data() as BoardInvitation);
} 

// Remove a membership document for a user and board
export async function removeBoardMembership(userId: string, boardId: string): Promise<void> {
  try {
    const membershipsQ = query(getMembershipsCollection(), where('userId', '==', userId), where('boardId', '==', boardId));
    const membershipsSnap = await getDocs(membershipsQ);
    for (const docSnap of membershipsSnap.docs) {
      await deleteDoc(docSnap.ref);
    }
  } catch (error) {
    console.error('Failed to remove board membership:', error);
  }
} 